import type { Iso2 } from 'intl-tel-input'
import { isString } from 'remeda'
import { focusWhenReady } from '../helpers/autofocus'
import { bindLiveProps, type LivePropValues } from '../helpers/live-props'
import {
  createTelInputController,
  parseCountryList,
  TEL_INPUT_VALIDATION_ERROR,
  type TelInputController,
  type TelInputValue
} from '../helpers/telephone'
import { triggerForm } from '../helpers/utils'

export type { TelInputValue } from '../helpers/telephone'

// Private types ---------------------------------------------------------------

type TelInputHookState = Readonly<{
  cleanup: () => void
  sync: () => void
}>

type TelInputHook = {
  readonly el: HTMLElement
  readonly pushEvent: (event: string, payload: Record<string, never>) => unknown
  telInputState?: TelInputHookState
}

// Private functions -----------------------------------------------------------

// Finds the hidden native input that submits the canonical value.
const getHiddenInput = (container: HTMLElement): HTMLInputElement | null =>
  container.querySelector<HTMLInputElement>('input[type="hidden"]')

// Finds the visible telephone input controlled by intl-tel-input.
const getTelInput = (container: HTMLElement): HTMLInputElement | null =>
  container.querySelector<HTMLInputElement>('input[type="tel"]')

// Reads the rendered validation message with a stable fallback.
const validationError = (container: HTMLElement): string =>
  container.dataset.validationError?.trim() || TEL_INPUT_VALIDATION_ERROR

// Reads a boolean dataset flag.
const enabled = (value: string | undefined): boolean => value === 'true'

// Mirrors the component's external form association onto a native input.
const setFormAssociation = (
  input: HTMLInputElement,
  formId: string | undefined
): void => {
  if (formId) {
    input.setAttribute('form', formId)
  } else {
    input.removeAttribute('form')
  }
}

// Validates and publishes the visible telephone value to the hidden input.
const syncValue = (
  container: HTMLElement,
  telInput: HTMLInputElement,
  controller: TelInputController
): TelInputValue => {
  const value = controller.getValue()
  telInput.setCustomValidity(
    value.status === 'invalid' ? validationError(container) : ''
  )

  const hidden = getHiddenInput(container)
  if (!hidden || value.status === 'invalid') {
    return value
  }

  hidden.value = value.status === 'valid' ? value.value : ''
  triggerForm(hidden.id, 'input', 'Tel input sync failed', container)
  return value
}

// Public hook -----------------------------------------------------------------

export const FacadeTelInput = {
  // Initializes intl-tel-input and connects its value to live props.
  async mounted(this: TelInputHook): Promise<void> {
    const container = this.el
    const telInput = getTelInput(container)
    if (!telInput) {
      return
    }

    const initialCountry = (container.dataset.country?.toLowerCase() ??
      'ua') as Iso2
    const locale = container.dataset.locale ?? 'uk'
    const onlyCountries = parseCountryList(container.dataset.onlyCountries)

    const hidden = getHiddenInput(container)
    const existingValue = hidden?.value ?? ''
    const originalReadOnly = telInput.readOnly
    let initialized = false

    // Mirrors rendered required, disabled, readonly, and form state.
    const syncSemanticState = (): void => {
      const disabled = enabled(container.dataset.disabled)

      telInput.required = enabled(container.dataset.required)
      telInput.disabled = disabled
      telInput.readOnly = !initialized || enabled(container.dataset.readonly)
      telInput.autofocus = enabled(container.dataset.autofocus)
      telInput.setAttribute(
        'autocomplete',
        container.dataset.autocomplete ?? 'tel'
      )
      setFormAssociation(telInput, container.dataset.form)

      if (hidden) {
        hidden.disabled = disabled
        setFormAssociation(hidden, container.dataset.form)
      }
    }

    // The visible tel input is an internal control - the Phoenix form is driven
    // only by the hidden input via syncValue(). intl-tel-input dispatches an
    // `input` event during setNumber() (and while formatting as you type), and it
    // bubbles to the form's phx-change; without this it fires a phantom update +
    // audit on mount. Attached before setNumber() so the initial format never
    // leaks, and does not block onInput (same-element listeners still run).
    const stopBubbling = (event: Event): void => event.stopPropagation()
    telInput.addEventListener('input', stopBubbling)
    telInput.addEventListener('change', stopBubbling)

    syncSemanticState()
    let disposed = false
    let controller: TelInputController | undefined
    let restoringFormValue = false
    const initialization = new AbortController()

    // Removes the listeners that isolate the internal visible input.
    const removeBoundaryListeners = (): void => {
      telInput.removeEventListener('input', stopBubbling)
      telInput.removeEventListener('change', stopBubbling)
    }

    // Cancels initialization when LiveView removes the hook early.
    const cleanupPending = (): void => {
      disposed = true
      initialization.abort()
      telInput.readOnly = originalReadOnly
      removeBoundaryListeners()
      controller?.destroy()
      this.telInputState = undefined
    }
    this.telInputState = {
      cleanup: cleanupPending,
      sync: syncSemanticState
    }

    try {
      controller = await createTelInputController(telInput, {
        initialCountry,
        locale,
        onlyCountries,
        signal: initialization.signal
      })
    } catch (error: unknown) {
      if (disposed) {
        return
      }

      this.telInputState?.cleanup()
      throw error
    }

    if (disposed) {
      controller.destroy()
      return
    }

    controller.setValue(existingValue)

    // Reads the best available local draft representation.
    const currentDraftValue = (): string => {
      const value = controller.getValue()

      if (value.status === 'valid') {
        return value.value
      }

      return value.status === 'empty' ? '' : telInput.value
    }

    // Mirrors controller validity onto the visible native input.
    const syncValidity = (): void => {
      const value = controller.getValue()
      telInput.setCustomValidity(
        value.status === 'invalid' ? validationError(container) : ''
      )
    }

    // Applies one canonical server value without emitting a local edit.
    const restoreServerValue = (value: string): void => {
      const current = controller.getValue()
      const currentValue =
        current.status === 'valid'
          ? current.value
          : current.status === 'empty'
            ? ''
            : undefined

      if (hidden) {
        hidden.setAttribute('value', value)
        hidden.value = value
      }

      if (currentValue === value) {
        syncValidity()
        return
      }

      restoringFormValue = true
      try {
        controller.setValue(value)
      } finally {
        restoringFormValue = false
      }
      syncValidity()
    }
    const cleanupLiveProps = bindLiveProps(container, {
      read: (properties): LivePropValues =>
        properties.includes('value') ? { value: currentDraftValue() } : {},
      apply: (values) => {
        if (isString(values.value)) {
          restoreServerValue(values.value)
        }
      }
    })

    // Publishes user edits unless a server restore caused the event.
    const syncUserValue = (): void => {
      if (!restoringFormValue) {
        const value = syncValue(container, telInput, controller)
        const invalidEvent = container.dataset.invalidEvent

        if (value.status === 'invalid' && invalidEvent) {
          this.pushEvent(invalidEvent, {})
        }
      }
    }

    // Validates and submits the associated form on Enter.
    const handleKeydown = (event: KeyboardEvent): void => {
      if (event.key === 'Enter') {
        event.preventDefault()
        const value = syncValue(container, telInput, controller)
        if (value.status === 'invalid' || !telInput.checkValidity()) {
          return
        }
        const form = hidden?.form
        if (form) {
          form.dispatchEvent(
            new Event('submit', { bubbles: true, cancelable: true })
          )
        }
      }
    }

    telInput.addEventListener('input', syncUserValue)
    telInput.addEventListener('keydown', handleKeydown)
    telInput.addEventListener('countrychange', syncUserValue)

    // Reconciles rendered semantic state after a LiveView patch.
    const sync = (): void => {
      syncSemanticState()
    }

    // Releases listeners, adapter state, and the third-party controller.
    const cleanup = (): void => {
      disposed = true
      initialization.abort()
      telInput.readOnly = originalReadOnly
      removeBoundaryListeners()
      telInput.removeEventListener('input', syncUserValue)
      telInput.removeEventListener('keydown', handleKeydown)
      telInput.removeEventListener('countrychange', syncUserValue)
      cleanupLiveProps()
      controller.destroy()
      this.telInputState = undefined
    }
    this.telInputState = { cleanup, sync }
    initialized = true
    syncValidity()
    sync()
    focusWhenReady(telInput)
  },

  // Reconciles semantic state after a LiveView patch.
  updated(this: TelInputHook): void {
    this.telInputState?.sync()
  },

  // Releases initialization or the active controller.
  destroyed(this: TelInputHook): void {
    this.telInputState?.cleanup()
  }
}
